/**
 * BDA-6157 / BDA-6247 — a flat Kennzahl catalog as the TREE the picker's dialog
 * renders, built from what the backend declares about it.
 *
 * The flat list is fine for the metrics a user can name and hopeless for the
 * ones they cannot: from the QA kick-off of 16.09.2026, someone after the
 * liquiden Mittel searches for "liquide Mittel" and never reaches
 * "Kassenbestand", because a search only helps a reader who already knows the
 * term. The tree answers that by letting them click down through a structure
 * instead — and the structure has to come from somewhere.
 *
 * ## Why this is not hand-maintained any more
 *
 * It was, until BDA-6247. The first cut of this dialog carried the
 * Jahresabschluss nesting as a table in the frontend, because
 * `available-operating-numbers` shipped one flat category level and the Cube's
 * two catalogs shipped no levels at all. That table was a standing liability:
 * a Finanzkennzahl added by the backend landed in the tree with no section
 * until the frontend caught up, and the Cube could never show more than one
 * level no matter what its data looked like. The backend now states the
 * structure itself — `section` and `parentId` on every entry — so the tree is
 * READ, not restated, and a catalog change reaches the UI on its own.
 *
 * ## The two shapes this has to serve
 *
 * The two cubes declare the same structure at different depths, and both go
 * through here so the dialog cannot look like two different controls:
 *
 * - **Klinik-Cube** (`clinics-api`): category → section → parent/child. Its
 *   categories are the ones the flat select already groups by.
 * - **Cube / ORIGIN** (`virtual-clinic-api`): section → parent/child. It has
 *   no category level at all, so there must not be one in its tree — a single
 *   anonymous top node would be a level of clicking that carries no
 *   information.
 *
 * Hence: the category level appears only if the catalog actually has one. It
 * is derived, not configured — a caller cannot get it wrong, and a catalog
 * that grows or loses categories needs no change here.
 */

/** A heading the catalog declares — a category or a section, resolved to one language. */
export interface MetricCatalogGrouping {
  id: string;
  label: string;
  /**
   * Ascending. Groupings without one sort last, keeping the order they first
   * appear in — see {@link metricCatalogTree} on why array position is not a
   * substitute.
   */
  order?: number | null;
}

/**
 * One catalog entry, already resolved to one language.
 *
 * Deliberately NOT either cube's wire type: `unit` is a plain string in
 * `clinics-api` and a `{de,en}` map in `virtual-clinic-api` (reported on
 * BDA-6247), and the label maps differ the same way. Resolving language and
 * unit in each cube's adapter keeps that difference at the wire edge, where it
 * belongs, instead of teaching this builder which endpoint it was fed from.
 */
export interface MetricCatalogEntry {
  id: string;
  label: string;
  unit?: string | null;
  /** Top level. Absent in catalogs that have none — see the file docs. */
  category?: MetricCatalogGrouping | null;
  /** Second level, inside the category. */
  section?: MetricCatalogGrouping | null;
  /** The entry this one rolls up into, if any. */
  parentId?: string | null;
}

/**
 * One node of the rendered tree.
 *
 * A node either PICKS a metric ({@link metricId} set) or only structures the
 * ones below it ({@link metricId} null). A metric node may still have children:
 * `Anlagevermögen` is both a pickable Kennzahl and the parent of its three
 * roman-numeral positions, and dropping either half would cost the user
 * something real.
 */
export interface MetricCatalogTreeNode {
  /** Unique within the tree; a metric node's key is its metric id. */
  key: string;
  label: string;
  /** The metric picked by choosing this node, or null for a pure heading. */
  metricId: string | null;
  unit: string | null;
  children: MetricCatalogTreeNode[];
}

/** The heading an entry with no category of its own is filed under. */
const UNCATEGORISED_HEADING = 'Weitere';

/** The entries that share one heading, in the order they arrived. */
interface CubeCatalogBucket {
  grouping: MetricCatalogGrouping | null;
  entries: MetricCatalogEntry[];
}

/** Ascending by `order`, with the order-less last. Relies on a stable sort. */
function byOrder(
  a: MetricCatalogGrouping | null,
  b: MetricCatalogGrouping | null,
): number {
  return (
    (a?.order ?? Number.MAX_SAFE_INTEGER) -
    (b?.order ?? Number.MAX_SAFE_INTEGER)
  );
}

/**
 * The catalog as a tree.
 *
 * ## What decides the order
 *
 * **Headings** are sorted by their declared `order`, never by where they
 * appear in the array. `clinics-api` sorts its response parents-before-children
 * and nothing else — its Pflege sections arrive 50, 10, 20, 40, 30 — so
 * first-appearance order would render a deliberate sequence scrambled.
 * `virtual-clinic-api` already sorts by `section.order`, and reading the field
 * makes the two behave the same either way.
 *
 * **Metrics** keep the array order inside their heading, for both the roots and
 * each parent's children. That is the one ordering the backend states only
 * positionally, and it carries real meaning: the Finanzkennzahlen arrive in
 * statement order, so the Bilanz reads as a Bilanz and "Summe Aktiva" lands
 * after the positions it sums rather than between "Sachanlagen" and "Vorräte".
 * The flat select keeps sorting alphabetically instead — a lookup list is
 * scanned by name, a structure is read in its own sequence.
 *
 * ## What it refuses to lose
 *
 * A metric is reachable here whatever the catalog says about it. An entry whose
 * `parentId` names something missing — the parent gated away, or simply not a
 * benchmarkable metric, as `g18`'s children are — becomes a root of its own
 * section rather than disappearing with it. Same for an entry with no section
 * in a category that has them: it hangs directly under the category.
 *
 * A heading exists only where metrics do, because every heading is built FROM
 * the entries under it: a permission that hides a whole family (the
 * Finanzkennzahlen need `ANNUAL_ACCOUNTS:BASE`, and are then omitted from the
 * response entirely) leaves no empty section behind rather than one that has
 * to be pruned afterwards.
 */
export function metricCatalogTree(
  entries: readonly MetricCatalogEntry[],
): MetricCatalogTreeNode[] {
  const nodes = new Map<string, MetricCatalogTreeNode>();
  for (const entry of entries) {
    nodes.set(entry.id, {
      key: entry.id,
      label: entry.label,
      metricId: entry.id,
      unit: entry.unit ?? null,
      children: [],
    });
  }

  const parentOf = new Map<string, string>();
  for (const entry of entries) {
    if (
      entry.parentId &&
      entry.parentId !== entry.id &&
      nodes.has(entry.parentId)
    ) {
      parentOf.set(entry.id, entry.parentId);
    }
  }

  /**
   * Whether attaching `id` under `parentId` would close a cycle.
   *
   * Nothing on the wire has one today — both catalogs are acyclic and ship
   * parents first. It is checked anyway because the cost of being wrong is not
   * a misplaced node but a tree that never finishes rendering, and this is the
   * only place that could catch it.
   */
  const wouldCycle = (id: string, parentId: string): boolean => {
    let cursor: string | undefined = parentId;
    while (cursor) {
      if (cursor === id) return true;
      cursor = parentOf.get(cursor);
    }
    return false;
  };

  const rootEntries: MetricCatalogEntry[] = [];
  for (const entry of entries) {
    const node = nodes.get(entry.id);
    if (!node) continue;

    const parentId = parentOf.get(entry.id);
    const parent =
      parentId && !wouldCycle(entry.id, parentId)
        ? nodes.get(parentId)
        : undefined;

    if (parent) {
      // Children in array order, under the parent wherever the parent landed —
      // a child never has to agree with its parent about the section.
      parent.children.push(node);
    } else {
      rootEntries.push(entry);
    }
  }

  /** Groups roots by one heading level, headings in their declared order. */
  const bucketBy = (
    roots: readonly MetricCatalogEntry[],
    groupingOf: (
      entry: MetricCatalogEntry,
    ) => MetricCatalogGrouping | null | undefined,
  ): CubeCatalogBucket[] => {
    const buckets = new Map<string, CubeCatalogBucket>();
    for (const entry of roots) {
      const grouping = groupingOf(entry) ?? null;
      // One bucket for everything ungrouped, keyed apart from any real id.
      const key = grouping ? grouping.id : ' ungrouped';
      const bucket = buckets.get(key) ?? { grouping, entries: [] };
      bucket.entries.push(entry);
      buckets.set(key, bucket);
    }
    return [...buckets.values()].sort((a, b) =>
      byOrder(a.grouping, b.grouping),
    );
  };

  const nodesOf = (bucket: CubeCatalogBucket): MetricCatalogTreeNode[] =>
    bucket.entries.flatMap((entry) => {
      const node = nodes.get(entry.id);
      return node ? [node] : [];
    });

  /** The section level of one category — or of the whole tree, when there is none. */
  const sectionsOf = (
    roots: readonly MetricCatalogEntry[],
    keyPrefix: string,
  ): MetricCatalogTreeNode[] =>
    bucketBy(roots, (entry) => entry.section).flatMap((bucket) => {
      const children = nodesOf(bucket);
      // An entry with no section hangs under the level above rather than under
      // a heading this file would have had to invent for it.
      if (!bucket.grouping) return children;

      return [
        {
          key: `${keyPrefix}${bucket.grouping.id}`,
          label: bucket.grouping.label,
          metricId: null,
          unit: null,
          children,
        },
      ];
    });

  // Categories exist only if the catalog declares them — see the file docs.
  if (!entries.some((entry) => entry.category)) {
    return sectionsOf(rootEntries, 'section-');
  }

  return bucketBy(rootEntries, (entry) => entry.category).map((bucket) => {
    const categoryId = bucket.grouping?.id ?? 'uncategorised';

    return {
      key: `category-${categoryId}`,
      label: bucket.grouping?.label ?? UNCATEGORISED_HEADING,
      metricId: null,
      unit: null,
      children: sectionsOf(bucket.entries, `section-${categoryId}-`),
    };
  });
}
